import clsx from 'clsx';
import type { JSX } from 'react';

import type { PrintDay, PrintRow, PrintStatus } from './types';
import { PRINT_STATUS_LABELS } from './helpers/printStatus';

/** Five columns on every sheet: none of the list endpoints return a phone number. */
const COLUMNS = [
  { key: 'time', label: 'Time', width: '10%' },
  { key: 'patient', label: 'Patient', width: '24%' },
  { key: 'service', label: 'Service', width: '32%' },
  { key: 'room', label: 'Room', width: '17%' },
  { key: 'status', label: 'Status', width: '17%' },
] as const;

const CELL_CLASSES =
  'vtwo:px-[6px] vtwo:py-[5px] vtwo:border-b-[0.75px] vtwo:border-[#e2e2e2] vtwo:align-top vtwo:break-words';

interface PrintDayTableProps {
  day: PrintDay;
}

/**
 * One table per day. The day band and the column headers both live in <thead>, so the browser
 * repeats them at the top of every page the day spills onto; no JavaScript pagination needed.
 */
export default function PrintDayTable({ day }: PrintDayTableProps): JSX.Element {
  const count = day.rows.length;
  const countLabel = `${count} ${count === 1 ? 'appointment' : 'appointments'}`;

  return (
    <table className="calendar-print__table vtwo:w-full vtwo:table-fixed vtwo:border-collapse vtwo:mb-[12px] vtwo:text-[9.6px] vtwo:text-print-ink">
      <colgroup>
        {COLUMNS.map((column) => (
          <col key={column.key} style={{ width: column.width }} />
        ))}
      </colgroup>
      <thead>
        <tr>
          <th
            colSpan={COLUMNS.length}
            className="vtwo:text-left vtwo:border-b-[1.5px] vtwo:border-print-ink vtwo:pt-[9px] vtwo:pb-[4px] vtwo:text-[10.4px] vtwo:font-extrabold vtwo:tracking-[0.05em] vtwo:uppercase"
          >
            <span className="vtwo:flex vtwo:justify-between vtwo:items-baseline">
              <span>{day.label}</span>
              <span className="vtwo:text-[9px] vtwo:font-semibold vtwo:tracking-[0.02em] vtwo:normal-case vtwo:text-print-muted">
                {countLabel}
              </span>
            </span>
          </th>
        </tr>
        <tr>
          {COLUMNS.map((column) => (
            <th
              key={column.key}
              className="vtwo:text-left vtwo:px-[6px] vtwo:pt-[5px] vtwo:pb-[4px] vtwo:border-b-[0.75px] vtwo:border-[#bbb] vtwo:text-[8.2px] vtwo:font-bold vtwo:tracking-[0.07em] vtwo:uppercase vtwo:text-print-muted"
            >
              {column.label}
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {day.rows.map((row) => (
          <PrintRowCells key={row.id} row={row} />
        ))}
      </tbody>
    </table>
  );
}

interface PrintRowCellsProps {
  row: PrintRow;
}

function PrintRowCells({ row }: PrintRowCellsProps) {
  const isCancelled = row.status === 'CANCELLED';

  return (
    <tr>
      <td
        className={clsx(
          CELL_CLASSES,
          'vtwo:whitespace-nowrap vtwo:font-bold vtwo:tabular-nums',
          isCancelled && 'vtwo:line-through vtwo:text-[#666]'
        )}
      >
        {row.startTime}
        <br />
        {row.endTime}
      </td>
      <td
        className={clsx(
          CELL_CLASSES,
          'vtwo:font-semibold',
          row.isUnassigned && 'vtwo:italic vtwo:font-normal'
        )}
      >
        {row.patient}
      </td>
      <td className={CELL_CLASSES}>{row.service}</td>
      <td className={clsx(CELL_CLASSES, 'vtwo:whitespace-nowrap')}>{row.room}</td>
      <td className={CELL_CLASSES}>
        <StatusPill status={row.status} />
      </td>
    </tr>
  );
}

interface StatusPillProps {
  status: PrintStatus;
}

/** Bordered greyscale pill: pharmacies print on mono lasers, where a colour badge disappears. */
function StatusPill({ status }: StatusPillProps) {
  return (
    <span
      className={clsx(
        'calendar-print__pill vtwo:inline-block vtwo:rounded-[2px] vtwo:border vtwo:border-print-muted vtwo:px-[5px] vtwo:py-[1px] vtwo:text-[7.8px] vtwo:font-bold vtwo:tracking-[0.05em] vtwo:uppercase vtwo:whitespace-nowrap',
        status === 'NEEDS_OUTCOME' && 'vtwo:border-2 vtwo:border-black vtwo:bg-[#eee]',
        status === 'CANCELLED' && 'vtwo:border-dashed vtwo:text-print-muted'
      )}
    >
      {PRINT_STATUS_LABELS[status]}
    </span>
  );
}
